/**
 * Fase 4 · CMS — hook `useContent(pageKey)`.
 *
 * Lee `GET /api/content/{page_key}` con:
 *   - Cache en memoria (module-level) para no golpear el backend en cada
 *     render, ni cuando el usuario navega y vuelve.
 *   - TTL de 60 s (mismo que el `Cache-Control` público del backend).
 *   - Fallback silencioso a los defaults hardcoded si la request falla.
 *
 * Uso:
 *   const { fields, loading } = useContent("home");
 *   const heroTitle = fields.hero_title || "Bienvenidos";
 */
import { useEffect, useState } from "react";
import { conditionalGet, invalidateConditional } from "./conditional-fetch";

const _cache = new Map(); // pageKey -> {fields, fetchedAt, updatedAt}
const _pending = new Map(); // pageKey -> Promise
const TTL_MS = 60 * 1000;

// Los defaults reflejan los strings actuales de la app; se usan como
// último recurso si el endpoint no responde. Se actualizan en tándem con
// `backend/routers/site_content.py::PAGE_DEFAULTS`.
export const CONTENT_DEFAULTS = {
  home: {
    hero_title: "",
    hero_subtitle: "",
    hero_image_url: "",
    hero_cta_label: "",
    seo_title: "MercaGo · Envío a los campamentos saharauis",
    seo_description:
      "Envía paquetes a los campamentos saharauis desde 35 € con 10 kg incluidos.",
  },
  envio: {
    banner_title: "Tienda MercaGo",
    banner_subtitle:
      "Elige del catálogo Mercadona lo que quieres enviar a tu familia.",
    banner_image_url: "",
    seo_title: "Tienda MercaGo · Catálogo Mercadona",
    seo_description:
      "Explora el catálogo Mercadona en MercaGo y envía productos halal a los campamentos saharauis.",
  },
  local: {
    banner_title: "Tienda local en los campamentos",
    banner_subtitle: "Compra desde los campamentos con entrega en Tindouf.",
    banner_image_url: "",
    seo_title: "MercaGo Local · Tienda en los campamentos",
    seo_description:
      "Compra desde los campamentos saharauis y recibe en tu daira.",
  },
  about: {
    title: "Sobre MercaGo",
    body_md: "",
    seo_title: "Sobre nosotros · MercaGo",
    seo_description: "Conoce MercaGo.",
  },
  shipping_terms: {
    title: "Condiciones de envío",
    body_md: "",
    seo_title: "Condiciones de envío · MercaGo",
    seo_description: "Consulta las condiciones de envío de MercaGo.",
  },
  shipment_wizard: {
    intro_title: "Nuevo envío",
    intro_subtitle: "",
    faq_items: [],
    seo_title: "Enviar un paquete · MercaGo",
    seo_description: "Envía un paquete a los campamentos saharauis con MercaGo.",
  },
  footer: {
    about_text: "Tu súper online — entregamos a tu familia en los campamentos saharauis.",
    contact_email: "info@mercago.one",
    contact_phone: "",
    whatsapp_link: "",
    social_links: [],
  },
};

async function _fetchContent(pageKey) {
  const cached = _cache.get(pageKey);
  const now = Date.now();
  if (cached && now - cached.fetchedAt < TTL_MS) return cached;

  if (_pending.has(pageKey)) return _pending.get(pageKey);

  // Nota: ``conditionalGet`` mantiene su propio cache HTTP con ETag +
  // If-None-Match. Este cache module-level añade además un TTL de 60 s
  // para evitar incluso la request condicional cuando la data es fresca.
  const p = conditionalGet(`/content/${pageKey}`)
    .then(({ data }) => {
      const entry = {
        fields: { ...(CONTENT_DEFAULTS[pageKey] || {}), ...(data?.fields || {}) },
        fetchedAt: Date.now(),
        updatedAt: data?.updated_at || null,
      };
      _cache.set(pageKey, entry);
      _pending.delete(pageKey);
      return entry;
    })
    .catch((_e) => {
      _pending.delete(pageKey);
      // Fallback silencioso a defaults; no cacheamos el error para que
      // reintente en el siguiente ciclo.
      return {
        fields: { ...(CONTENT_DEFAULTS[pageKey] || {}) },
        fetchedAt: Date.now(),
        updatedAt: null,
        error: true,
      };
    });

  _pending.set(pageKey, p);
  return p;
}

/**
 * Invalida el cache de una página (o de todas si no se pasa key). Útil tras
 * un save admin para que el sitio muestre inmediatamente los cambios.
 */
export function invalidateContent(pageKey) {
  if (pageKey) {
    _cache.delete(pageKey);
    invalidateConditional(`/content/${pageKey}`);
  } else {
    _cache.clear();
    invalidateConditional();
  }
}

export default function useContent(pageKey) {
  const [state, setState] = useState(() => {
    const cached = _cache.get(pageKey);
    if (cached) return { fields: cached.fields, loading: false, error: false };
    return {
      fields: { ...(CONTENT_DEFAULTS[pageKey] || {}) },
      loading: true,
      error: false,
    };
  });

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: !s.fields || !_cache.has(pageKey) }));
    _fetchContent(pageKey).then((entry) => {
      if (!alive) return;
      setState({
        fields: entry.fields,
        loading: false,
        error: !!entry.error,
      });
    });
    return () => {
      alive = false;
    };
  }, [pageKey]);

  return state;
}
